import { Checkbox, type CheckboxProps } from '@heroui/react';
import React from 'react';

export interface ChocoCheckboxProps extends Omit<CheckboxProps, 'classNames'> {
  /** Custom class name for the checkbox wrapper */
  className?: string;
  /** Label for the checkbox */
  label?: React.ReactNode;
  /** Description text that appears below the label */
  description?: string;
  /** Error message to display */
  errorMessage?: string;
  /** Whether the checkbox is required */
  isRequired?: boolean;
  /** Whether the checkbox is disabled */
  isDisabled?: boolean;
  /** Whether the checkbox is in an invalid state */
  isInvalid?: boolean;
}

/**
 * A standardized checkbox component that follows Choco's design system.
 * Built on top of HeroUI's Checkbox with consistent styling and accessibility.
 *
 * @example
 * ```tsx
 * <ChocoCheckbox
 *   label="Accept terms"
 *   description="By checking this box, you agree to our terms of service"
 *   isRequired
 *   onChange={(checked) => console.log('Checked:', checked)}
 * />
 * ```
 */
export const ChocoCheckbox: React.FC<ChocoCheckboxProps> = ({
  className = '',
  label,
  description,
  errorMessage,
  isRequired = false,
  isDisabled = false,
  isInvalid = false,
  ...props
}) => {
  const id = React.useId();
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;

  const hasError = isInvalid || !!errorMessage;

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <Checkbox
        {...props}
        id={id}
        isDisabled={isDisabled}
        isInvalid={hasError}
        aria-labelledby={labelId}
        aria-describedby={description ? descriptionId : undefined}
        aria-errormessage={errorMessage ? errorId : undefined}
        aria-invalid={hasError}
        classNames={{
          base: 'inline-flex max-w-full gap-2',
          label: 'text-foreground',
          hiddenInput: 'h-6 m-2',
          // Square corners + a visible check mark: without them the control
          // reads as a radio button (independent booleans looked mutually
          // exclusive — bugbash feedback).
          wrapper:
            'rounded-md before:rounded-md after:rounded-md before:border-default-400',
        }}
      >
        {label && (
          <span id={labelId} className="flex items-center gap-1">
            {label}
            {isRequired && <span className="text-danger">*</span>}
          </span>
        )}
      </Checkbox>
      {description && (
        <p id={descriptionId} className="text-sm text-default-500 pl-7">
          {description}
        </p>
      )}
      {errorMessage && (
        <p id={errorId} className="text-sm text-danger pl-7">
          {errorMessage}
        </p>
      )}
    </div>
  );
};
